<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useToggle } from '@vueuse/core';
import ReportHeader from './components/ReportHeader.vue';
import MetricCard from './components/overview/MetricCard.vue';
import AgentTable from './components/overview/AgentTable.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import { useI18n } from 'vue-i18n';
import ConversationApi from 'dashboard/api/inbox/conversation';

const { t } = useI18n();
const store = useStore();

const agents = useMapGetter('agents/getAgents');
const agentStatus = useMapGetter('agents/getAgentStatus');
const agentConversationMetric = useMapGetter('getAgentConversationMetric');
const uiFlags = useMapGetter('getOverviewUIFlags');

const CONVERSATIONS_PER_PAGE = 5;
const statusFilter = ref('all');
const selectedAgentId = ref(null);
const agentConversations = ref([]);
const agentConversationsMeta = ref({ all_count: 0 });
const conversationsLoading = ref(false);
const agentConversationsPage = ref(1);
const [showAgentDropdown, toggleAgentDropdown] = useToggle();

const statusOptions = [
  { value: 'all', labelKey: 'AGENT_MONITOR.STATUS_FILTER.ALL' },
  { value: 'online', labelKey: 'AGENT_MONITOR.STATUS_FILTER.ONLINE' },
  { value: 'offline', labelKey: 'AGENT_MONITOR.STATUS_FILTER.OFFLINE' },
  { value: 'busy', labelKey: 'AGENT_MONITOR.STATUS_FILTER.BUSY' },
];

const filteredAgents = computed(() => {
  const list = agents.value || [];
  if (statusFilter.value === 'all') return list;
  return list.filter(a => a.availability_status === statusFilter.value);
});

const totalAgents = computed(() => agents.value?.length ?? 0);
const liveAgents = computed(
  () =>
    agents.value?.filter(a => a.availability_status === 'online').length ?? 0
);

const agentMenuItems = computed(() => {
  const list = agents.value || [];
  return list.map(agent => ({
    action: 'select',
    value: agent.id,
    label: agent.available_name || agent.name,
  }));
});

const selectedAgentLabel = computed(() => {
  if (!selectedAgentId.value)
    return t('AGENT_MONITOR.CONVERSATIONS_BY_AGENT.SELECT_AGENT');
  const agent = (agents.value || []).find(a => a.id === selectedAgentId.value);
  return agent
    ? agent.available_name || agent.name
    : t('AGENT_MONITOR.CONVERSATIONS_BY_AGENT.SELECT_AGENT');
});

const agentConversationsTotalPages = computed(() =>
  Math.ceil(
    (agentConversationsMeta.value.all_count || 0) / CONVERSATIONS_PER_PAGE
  )
);

const agentConversationsRangeText = computed(() => {
  const total = agentConversationsMeta.value.all_count || 0;
  if (total === 0) return '';
  const from = (agentConversationsPage.value - 1) * CONVERSATIONS_PER_PAGE + 1;
  const to = Math.min(
    agentConversationsPage.value * CONVERSATIONS_PER_PAGE,
    total
  );
  return t('AGENT_MONITOR.CONVERSATIONS_BY_AGENT.SHOWING_RANGE', {
    from,
    to,
    total,
  });
});

const fetchData = () => {
  store.dispatch('agents/get');
  store.dispatch('fetchAgentConversationMetric');
  if (selectedAgentId.value) {
    fetchAgentConversations(agentConversationsPage.value);
  }
};

const fetchAgentConversations = async (page = 1) => {
  if (!selectedAgentId.value) return;
  conversationsLoading.value = true;
  try {
    const payload = {
      queryData: {
        payload: [
          {
            attribute_key: 'assignee_id',
            filter_operator: 'equal_to',
            values: [selectedAgentId.value],
            query_operator: null,
          },
        ],
      },
      page,
      perPage: CONVERSATIONS_PER_PAGE,
    };
    const { data } = await ConversationApi.filter(payload);
    agentConversations.value = data.payload || [];
    agentConversationsMeta.value = data.meta || { all_count: 0 };
    agentConversationsPage.value = page;
  } catch {
    agentConversations.value = [];
    agentConversationsMeta.value = { all_count: 0 };
    useAlert(t('AGENT_MONITOR.CONVERSATIONS_BY_AGENT.ERROR'));
  } finally {
    conversationsLoading.value = false;
  }
};

const onAgentSelect = ({ value }) => {
  toggleAgentDropdown(false);
  selectedAgentId.value = value;
  agentConversationsPage.value = 1;
  fetchAgentConversations(1);
};

const onConversationsPageChange = page => {
  fetchAgentConversations(page);
};

watch(selectedAgentId, id => {
  if (!id) {
    agentConversations.value = [];
    agentConversationsMeta.value = { all_count: 0 };
  }
});

const { startRefetching } = useLiveRefresh(fetchData);

const isSummaryLoading = computed(
  () => uiFlags.value?.isFetchingAgentConversationMetric ?? false
);

onMounted(() => {
  fetchData();
  startRefetching();
});
</script>

<template>
  <div>
    <ReportHeader :header-title="$t('AGENT_MONITOR.TITLE')" />
    <div class="flex flex-col gap-4 pb-6">
      <MetricCard
        :header="$t('AGENT_MONITOR.SUMMARY')"
        :is-loading="isSummaryLoading"
        :loading-message="$t('AGENT_MONITOR.LOADING_MESSAGE')"
      >
        <div v-if="!isSummaryLoading" class="flex flex-col gap-6 w-full">
          <div class="grid grid-cols-2 gap-6 md:gap-8">
            <div class="flex-1 min-w-0">
              <h3 class="text-base font-medium text-n-slate-11 mb-1">
                {{ $t('AGENT_MONITOR.TOTAL_AGENTS') }}
              </h3>
              <p class="text-n-slate-12 text-3xl font-medium tracking-tight">
                {{ totalAgents }}
              </p>
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-base font-medium text-n-slate-11 mb-1">
                {{ $t('AGENT_MONITOR.LIVE_AGENTS') }}
              </h3>
              <p class="text-n-slate-12 text-3xl font-medium tracking-tight">
                {{ liveAgents }}
              </p>
            </div>
          </div>
          <div class="border-t border-n-weak pt-5">
            <h3 class="text-base font-medium text-n-slate-12 mb-4">
              {{ $t('OVERVIEW_REPORTS.AGENT_STATUS.HEADER') }}
            </h3>
            <div class="grid grid-cols-3 gap-6 md:gap-8">
              <div class="flex-1 min-w-0">
                <h4 class="text-sm font-medium text-n-slate-11 mb-1">
                  {{ $t('OVERVIEW_REPORTS.AGENT_STATUS.ONLINE') }}
                </h4>
                <p class="text-n-slate-12 text-2xl font-medium tracking-tight">
                  {{ agentStatus?.online ?? 0 }}
                </p>
              </div>
              <div class="flex-1 min-w-0">
                <h4 class="text-sm font-medium text-n-slate-11 mb-1">
                  {{ $t('OVERVIEW_REPORTS.AGENT_STATUS.BUSY') }}
                </h4>
                <p class="text-n-slate-12 text-2xl font-medium tracking-tight">
                  {{ agentStatus?.busy ?? 0 }}
                </p>
              </div>
              <div class="flex-1 min-w-0">
                <h4 class="text-sm font-medium text-n-slate-11 mb-1">
                  {{ $t('OVERVIEW_REPORTS.AGENT_STATUS.OFFLINE') }}
                </h4>
                <p class="text-n-slate-12 text-2xl font-medium tracking-tight">
                  {{ agentStatus?.offline ?? 0 }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </MetricCard>

      <MetricCard :header="$t('AGENT_MONITOR.AGENT_LIST_HEADER')">
        <template #control>
          <div class="flex items-center gap-2">
            <span class="text-n-slate-11 text-sm">
              {{ $t('AGENT_MONITOR.TABLE_HEADER.STATUS') }}:
            </span>
            <div
              class="flex gap-0.5 rounded-lg bg-n-solid-3 p-0.5 shadow outline outline-1 outline-n-container"
            >
              <button
                v-for="opt in statusOptions"
                :key="opt.value"
                type="button"
                class="rounded-md px-3 py-1.5 text-sm font-medium transition-colors"
                :class="[
                  statusFilter === opt.value
                    ? 'bg-n-solid-2 text-n-slate-12 shadow outline outline-1 outline-n-weak'
                    : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12',
                ]"
                @click="statusFilter = opt.value"
              >
                {{ $t(opt.labelKey) }}
              </button>
            </div>
          </div>
        </template>
        <AgentTable
          :agents="filteredAgents"
          :agent-metrics="agentConversationMetric || []"
          :is-loading="uiFlags?.isFetchingAgentConversationMetric ?? false"
        />
      </MetricCard>

      <MetricCard :header="$t('AGENT_MONITOR.CONVERSATIONS_BY_AGENT.HEADER')">
        <template #control>
          <div
            v-on-clickaway="() => toggleAgentDropdown(false)"
            class="relative flex items-center group z-50"
          >
            <Button
              sm
              slate
              faded
              :label="selectedAgentLabel"
              class="min-w-[12rem] capitalize rounded-md group-hover:bg-n-alpha-2"
              @click="toggleAgentDropdown()"
            />
            <DropdownMenu
              v-if="showAgentDropdown"
              :menu-items="agentMenuItems"
              class="mt-1 ltr:right-0 rtl:left-0 xl:ltr:right-0 xl:rtl:left-0 top-full max-h-60 overflow-y-auto"
              label-class="capitalize"
              @action="onAgentSelect"
            />
          </div>
        </template>
        <div v-if="!selectedAgentId">
          <EmptyState
            :title="
              $t('AGENT_MONITOR.CONVERSATIONS_BY_AGENT.NO_AGENT_SELECTED')
            "
          />
        </div>
        <template v-else>
          <div
            v-if="conversationsLoading"
            class="items-center flex text-base justify-center px-12 py-8"
          >
            <Spinner />
            <span class="text-n-slate-11 ml-2">
              {{ $t('AGENT_MONITOR.CONVERSATIONS_BY_AGENT.LOADING') }}
            </span>
          </div>
          <template v-else>
            <EmptyState
              v-if="agentConversations.length === 0"
              :title="
                $t('AGENT_MONITOR.CONVERSATIONS_BY_AGENT.NO_CONVERSATIONS')
              "
            />
            <div v-else class="flex flex-col gap-3">
              <p
                v-if="agentConversationsRangeText"
                class="text-n-slate-11 text-sm mb-1"
              >
                {{ agentConversationsRangeText }}
              </p>
              <div
                v-for="conv in agentConversations"
                :key="conv.id"
                class="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-n-weak bg-n-solid-2 px-5 py-3 text-sm shadow outline outline-1 outline-n-container"
              >
                <span class="font-medium text-n-slate-12">
                  #{{ conv.id }}
                </span>
                <span class="text-n-slate-11">
                  {{ $t('AGENT_MONITOR.CONVERSATIONS_BY_AGENT.CONTACT') }}:
                  <span class="text-n-slate-12">{{
                    conv.meta?.sender?.name || '—'
                  }}</span>
                </span>
                <span class="text-n-slate-11 capitalize">
                  {{ $t('AGENT_MONITOR.CONVERSATIONS_BY_AGENT.STATUS') }}:
                  <span class="text-n-slate-12">{{ conv.status || '—' }}</span>
                </span>
                <span class="text-n-slate-11">
                  {{ $t('AGENT_MONITOR.CONVERSATIONS_BY_AGENT.INBOX') }}:
                  <span class="text-n-slate-12">{{
                    conv.meta?.inbox?.name || conv.inbox_id || '—'
                  }}</span>
                </span>
              </div>
              <div
                v-if="agentConversationsTotalPages > 1"
                class="mt-3 flex items-center justify-center gap-3"
              >
                <Button
                  sm
                  slate
                  faded
                  :label="$t('AGENT_MONITOR.CONVERSATIONS_BY_AGENT.PREVIOUS')"
                  class="rounded-md"
                  :disabled="agentConversationsPage <= 1"
                  @click="onConversationsPageChange(agentConversationsPage - 1)"
                />
                <span class="text-n-slate-11 text-sm min-w-[4rem] text-center">
                  {{ agentConversationsPage }} /
                  {{ agentConversationsTotalPages }}
                </span>
                <Button
                  sm
                  slate
                  faded
                  :label="$t('AGENT_MONITOR.CONVERSATIONS_BY_AGENT.NEXT')"
                  class="rounded-md"
                  :disabled="
                    agentConversationsPage >= agentConversationsTotalPages
                  "
                  @click="onConversationsPageChange(agentConversationsPage + 1)"
                />
              </div>
            </div>
          </template>
        </template>
      </MetricCard>
    </div>
  </div>
</template>
